import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { APP_CONFIG } from '@/constants/branding';
import { useAuth } from '@/contexts/AuthContext';
import { certificateService } from '@/services/certificateService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Download, Award, CheckCircle } from 'lucide-react';
import CertificatePreview from '@/components/CertificatePreview';
import LanguageHreflang from '@/components/LanguageHreflang';
import { useTranslation } from '@/hooks/useTranslation';
import GlobalNavbar from '@/components/GlobalNavbar';
import Footer from '@/components/Footer';
import '@/styles/provatio-unified-design.css';

const PROGRAMS = [
  "Grafotécnica",
  "Perícia Contábil",
  "Perícia Judicial",
  "Análise Forense",
  "Documentoscopia",
  "Perícia Digital"
];

export default function CertificacaoPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Form State
  const [name, setName] = useState('');
  const [program, setProgram] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (user) {
      const metadataName = user.user_metadata?.full_name || user.user_metadata?.nome_completo || '';
      if (metadataName) setName(metadataName);
      loadCertificates();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadCertificates = async () => {
    if(!user) return;
    try {
      setLoading(true);
      const data = await certificateService.getUserCertificates();
      setCertificates(data || []);
    } catch (error) {
      console.error("Erro ao carregar certificados:", error);
      toast({ variant: "destructive", title: "Erro", description: "Não foi possível carregar seus certificados." });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!name || !program || !date) {
      toast({ variant: "destructive", title: "Atenção", description: "Preencha todos os campos obrigatórios." });
      return;
    }

    try {
      setGenerating(true);
      await certificateService.saveCertificate(user.id, name, program, new Date(date).toISOString());
      toast({ title: "Sucesso", description: "Certificado gerado com sucesso!" });
      await loadCertificates();
      setProgram('');
    } catch (error) {
      console.error("Erro ao gerar:", error);
      toast({ variant: "destructive", title: "Erro", description: "Falha ao gerar certificado." });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (cert) => {
    try {
      toast({ title: "Gerando PDF...", description: "Aguarde enquanto preparamos seu download." });
      await certificateService.generateCertificatePDF(cert);
    } catch (error) {
      console.error("Erro no download:", error);
      toast({ variant: "destructive", title: "Erro", description: "Falha ao baixar o certificado." });
    }
  };

  return (
    <div className="min-h-screen flex flex-col provatio-unified">
      <Helmet>
        <title>{t('certificacao.titulo')} - {APP_CONFIG.APP_NAME}</title>
      </Helmet>
      <LanguageHreflang />
      <GlobalNavbar />

      <main className="flex-1 container mx-auto p-6 md:p-12 max-w-7xl space-y-12">
        <div className="border-b border-[#E5E7EB] pb-6">
          <h1 className="text-3xl font-bold text-[#193D6D] mb-2 flex items-center gap-3">
             <Award className="h-8 w-8 text-[#D4AF37]" /> {t('certificacao.titulo')}
          </h1>
          <p className="text-[#6B7280]">{t('certificacao.descricao')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="pv-card border-none shadow-md">
            <div className="bg-[#193D6D] text-white p-6 rounded-t-xl border-b-4 border-[#D4AF37]">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Award className="h-5 w-5 text-[#D4AF37]" /> {t('certificacao.emitir')}
              </h3>
              <p className="text-white/80 text-sm mt-1">{t('certificacao.preencha')}</p>
            </div>
            <div className="p-6">
              <form onSubmit={handleGenerate} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700 font-semibold">{t('certificacao.nome')}</Label>
                  <input 
                    id="name" 
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    placeholder={t('certificacao.nome')}
                    className="pv-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="program" className="text-gray-700 font-semibold">{t('certificacao.programa')}</Label>
                  <Select value={program} onValueChange={setProgram}>
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder={t('certificacao.programa')} />
                    </SelectTrigger>
                    <SelectContent>
                      {PROGRAMS.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date" className="text-gray-700 font-semibold">{t('certificacao.data')}</Label>
                  <input 
                    id="date" 
                    type="date"
                    value={date} 
                    onChange={(e) => setDate(e.target.value)} 
                    className="pv-input"
                  />
                </div>

                <button 
                  type="submit" 
                  className="pv-btn pv-btn-primary w-full h-12 mt-4"
                  disabled={generating || !name || !program || !date || !user}
                >
                  {generating ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Award className="mr-2 h-5 w-5 text-[#D4AF37]" />}
                  {t('certificacao.gerarBtn')}
                </button>
              </form>
            </div>
          </div>

          <div className="flex flex-col space-y-4">
            <h3 className="font-semibold text-[#193D6D] text-lg border-b border-[#E5E7EB] pb-2">{t('certificacao.preview')}</h3>
            <div className="flex-1 overflow-hidden rounded-xl border border-[#E5E7EB] shadow-sm bg-[#F9FAFB] relative min-h-[400px]">
              <div className="absolute inset-0 overflow-auto no-scrollbar" style={{ transform: 'scale(0.85)', transformOrigin: 'top left', width: '118%', height: '118%' }}>
                  <CertificatePreview name={name} program={program} date={date} />
              </div>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-[#E5E7EB]">
          <h2 className="text-2xl font-bold text-[#193D6D] mb-6 flex items-center gap-2">
            <CheckCircle className="h-6 w-6 text-[#10B981]" /> {t('certificacao.meus')}
          </h2>
          
          {!user ? (
             <div className="pv-card text-center py-12 bg-white">
                <p className="text-gray-500 font-medium">Faça login para ver seus certificados.</p>
             </div>
          ) : loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#193D6D]" /></div>
          ) : certificates.length === 0 ? (
            <div className="pv-card border-dashed text-center py-16 bg-[#F9FAFB]">
                <Award className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">{t('certificacao.nenhum')}</p>
                <p className="text-sm text-gray-400 mt-1">{t('certificacao.utilize')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {certificates.map((cert) => (
                <div key={cert.id} className="pv-card flex flex-col">
                  <div className="h-2 bg-[#D4AF37] w-full"></div>
                  <div className="p-6 flex-1">
                    <div className="flex justify-between items-start mb-4">
                      <span className="pv-badge pv-badge-blue font-mono">
                        {cert.certificate_id}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[#193D6D] mb-1">{cert.certification_program}</h3>
                    <p className="text-gray-600 mb-4">{cert.professional_name}</p>
                    <div className="flex items-center text-sm text-gray-500 gap-2 mt-auto">
                      <CheckCircle className="h-4 w-4 text-[#10B981]" />
                      {t('certificacao.emitidoEm')}: {new Date(cert.certification_date).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                  <div className="bg-[#F9FAFB] p-4 border-t border-[#E5E7EB]">
                    <button 
                      className="pv-btn w-full border border-[#193D6D] text-[#193D6D] hover:bg-[#193D6D] hover:text-white"
                      onClick={() => handleDownload(cert)}
                    >
                      <Download className="mr-2 h-4 w-4" /> {t('certificacao.baixar')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}